import React, { useState } from 'react';
import { useIntl } from '@atlassian/jira-intl';
import { PRIORITY_PICKER_DROPDOWN_MENU_SELECTOR } from '../../../common/constants';
import PrioritiesPicker from '../../../common/ui/priority-picker/main.tsx';
import type { Priority } from '../../../common/ui/types';
import { usePriorities } from '../../../controllers/use-priorities';
import type { PrioritySchemeFilters } from '../../../services/priority-schemes-page/types';
import { messages } from './messages';

type PriorityFilterPickerProps = {
	selectedPriorityIds: ReadonlyArray<Priority['id']>;
	setSelectedPriorityIds: (priorityIds: PrioritySchemeFilters['priorityIds']) => void;
};

export const PriorityFilterPicker = ({
	selectedPriorityIds,
	setSelectedPriorityIds,
}: PriorityFilterPickerProps) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [pickerFilter, setPickerFilter] = useState('');
	const [pageIndex, setPageIndex] = useState(0);
	const { page, loading } = usePriorities(pickerFilter, pageIndex);

	const onFilterChange = (newFilter: string) => {
		setPickerFilter(newFilter);
	};

	const onLoadMore = () => {
		const { currentPage } = page;
		setPageIndex(currentPage + 1);
	};

	const onSelect = (priority: { id: Priority['id'] }) => {
		const newPriorities = selectedPriorityIds.includes(priority.id)
			? selectedPriorityIds.filter((id) => id !== priority.id)
			: [...selectedPriorityIds, priority.id];
		setSelectedPriorityIds(newPriorities);
	};

	const onClearSelection = () => {
		setSelectedPriorityIds([]);
	};

	const onOpen = () => {
		setIsOpen(true);
		setPageIndex(0);
	};

	const onClose = () => {
		setIsOpen(false);
		setSelectedPriorityIds(selectedPriorityIds);
	};

	const trigger: string =
		selectedPriorityIds.length === 0
			? intl.formatMessage(messages.choosePriorities)
			: intl.formatMessage(messages.multipleSelectedPriorities, {
					count: selectedPriorityIds.length,
				});

	return (
		<PrioritiesPicker
			showCheckboxes
			selectedPriorityIds={selectedPriorityIds}
			filter={pickerFilter}
			renderTrigger={trigger}
			isLoading={loading}
			page={page}
			isOpen={isOpen}
			onSelect={onSelect}
			onClearSelection={onClearSelection}
			onFilterChange={onFilterChange}
			onClose={onClose}
			onLoadMore={onLoadMore}
			onOpen={onOpen}
			testId={PRIORITY_PICKER_DROPDOWN_MENU_SELECTOR}
		/>
	);
};
